import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';
import { dateOnlyRangeClause } from '@/lib/utils/custom-date/custom-date';

/**
 * The Data Gateway's `get<Schema>s` queries take native `where` / `order` / `paging` arguments
 * instead of a single JSON-string `input: DynamicQueryInput` — see graphql-query-builder.ts for
 * why these are inlined as literals rather than passed as typed variables. Paged lists sort on the
 * server (`buildOrderLiteral`), so each page is a slice of the sorted whole.
 */
/**
 * Latest lunch day first by default, like the old request list's `SortKey=RequestedForDateTime`
 * descending.
 */
export const DEFAULT_MY_LUNCH_REQUESTS_ORDER: OrderBy[] = [
  { field: 'RequestedForDate', direction: 'DESC' },
];

/**
 * The lunch page's state in one read: the caller's order for `day` ("YYYY-MM-DD", at most one) and the
 * active menus of its weekday. The gateway answers both root fields in the time of one read.
 */
export const buildGetLunchStatusQuery = (userId: string, day: string, weekDay: string) => `
  query {
    Orders: getLunchRequests(
      where: ${buildWhereLiteral({ UserId: userId, RequestedForDate: dateOnlyRangeClause(day, day) })}
      order: []
      paging: ${buildPagingLiteral(1, 1)}
    ) {
      items {
        ItemId
        MenuName
        MenuItems
      }
    }
    Menus: getLunchMenus(
      where: ${buildWhereLiteral({ WeekDay: weekDay, IsActive: true })}
      order: []
      paging: ${buildPagingLiteral(1, 20)}
    ) {
      items {
        ItemId
        Name
        WeekDay
        MenuItems
        IsActive
      }
    }
  }
`;

export const buildGetMyLunchRequestsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_MY_LUNCH_REQUESTS_ORDER
) => `
  query {
    LunchRequests: getLunchRequests(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        UserId
        DisplayName
        DepartmentName
        DepartmentKey
        MenuId
        MenuName
        MenuItems
        RequestedForDate
        CreatedDate
      }
    }
  }
`;
